import React, { useState, useMemo, useEffect, useCallback } from "react";
import SupervisorApi from "../../utility/supervisorApi";
import { useQuery } from "@tanstack/react-query";
import Pagination from "../../components/Pagination";
import debounce from "lodash-es/debounce";
import SelectLabels from "../../components/SelectLabels";
import { useMutation } from "@tanstack/react-query";

function UserManagement() {
  const { fetchData, createData } = SupervisorApi();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [timeRange, setTimeRange] = useState(0);
  const [searched, setSearched] = useState("");
  const [, setNewPeriod] = useState("7days");

  const period = [
    { id: 0, key: "7days", time: "One Week" },
    { id: 1, key: "30days", time: "One Month" },
    { id: 2, key: "12months", time: "One Year" },
  ];

  const debouncedSearch = useMemo(
    () =>
      debounce((search) => {
        setSearched(search);
      }, 500),
    []
  );

  const { data } = useQuery({
    queryKey: ["userManagement", { searched, currentPage }],
    queryFn: () => fetchData("user-management", currentPage, searched),
    retry: 1,
    keepPreviousData: true,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const chartData = useMutation({
    mutationFn: (newPeriod) =>
      createData(`user-management?date_filter=${newPeriod}`, null, true),
    onSuccess: (data) => {
      setUsersData(data?.data);
      setTotalPage(data?.data?.pagination?.last_page);
    },
  });

  useEffect(() => {
    const selected = period[timeRange];
    if (selected) {
      setNewPeriod(selected.key);
      chartData.mutate(selected.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const handleChange = (value) => {
    setTimeRange(value);
  };

  useEffect(() => {
    if (data) {
      setUsersData(data);
      setTotalPage(data?.pagination?.last_page);
    }
  }, [data]);

  // Reset current page when searched changes
  useEffect(() => {
    if (searched) {
      setCurrentPage(1);
    }
  }, [searched]);

  // Memoize handlePageChange
  const handlePageChange = useCallback(
    (page) => {
      if (page === currentPage) return;

      if (page > currentPage) {
        setIsNextLoading(true);
        setIsPrevLoading(false);
      } else {
        setIsPrevLoading(true);
        setIsNextLoading(false);
      }

      setCurrentPage(page);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    },
    [currentPage]
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearched(value);
      debouncedSearch(value);
    },
    [setSearched, debouncedSearch]
  );

  return (
    <div className="min-w-full h-screen overflow-y-auto pb-24">
      <section class="m-4 ">
        <div class=" mb-4">
          <h2 class="text-2xl font-semibold ">User Management</h2>
        </div>
        <div class="bg-white p-4 rounded shadow">
          <div class="flex items-center justify-between mb-4 ">
            <input
              class="flex-grow p-2 border rounded"
              placeholder="Search"
              type="text"
              value={searched}
              onChange={handleSearchChange}
              className="border-2 rounded-sm border-gray-400 w-60 p-1.5 px-4"
            />
            <div>
              <SelectLabels
                valueKey="id"
                labelKey="time"
                text="Select a period"
                categoryData={period}
                onSelectChange={handleChange}
                previousValue={0}
              />
            </div>
          </div>
          {/* Use that of dashboard content */}
          <table class="w-full text-left">
            <thead>
              <tr class="border-b">
                <th className="py-2 px-4 border-b text-center border-gray-300 text-wrap">
                  Name
                </th>
                <th className="py-2 px-4 border-b text-center border-gray-300 text-wrap">
                  Role
                </th>
                <th className="py-2 px-4 border-b text-center border-gray-300 text-wrap">
                  Store
                </th>
                <th className="py-2 px-4 border-b text-center border-gray-300 text-wrap">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {usersData?.users?.map((item, index) => (
                <tr key={item?.id}>
                  <td className=" border-b py-2 px-4 text-center text-wrap">
                    {item?.firstName} {item?.lastName}
                  </td>
                  <td className=" border-b py-2 px-4 text-center text-wrap">
                    {item?.roleName}
                  </td>
                  <td className=" border-b py-2 px-4 text-center text-wrap">
                    {item?.storeName}
                  </td>

                  <td className="border-b py-2 px-4 text-center text-wrap ">
                    {" "}
                    <div
                      className={`p-1 rounded-md text-white ${
                        Number(item?.status) === 1
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    >
                      {Number(item?.status) === 1 ? "Active" : "Inactive"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            onPageChange={handlePageChange}
            totalPage={totalPage}
            currentPage={currentPage}
            setIsNextLoading={setIsNextLoading}
            setIsPrevLoading={setIsPrevLoading}
            isPrevLoading={isPrevLoading}
            isNextLoading={isNextLoading}
          />
        </div>
      </section>
      {/* <section className="m-4 mt-0">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-2xl font-semibold">Store performance overview</h2>
          <button class="hover:cursor-pointer border border-black px-4 py-2 rounded">
            Filter
          </button>
        </div>
      </section> */}
    </div>
  );
}

export default UserManagement;
